// Period + proximity formatter for the SOW table Period column.
// Renders `15 Apr 2026 → 30 Sep 2026` plus a status/proximity
// meta-line ("in progress · 5 months remaining", "completed 4 weeks ago",
// "starts in 5 months", "overdue · Nd past end date").
//
// Status transitions are user-driven, not date-driven (per brief §6 + brand
// SKILL.md §9.1) — a SOW with end_date in the past can still be `active` if
// work is not formally completed. We surface the slippage ("overdue · …")
// in the meta-line but never auto-transition the pill.

import { humaniseDays } from "../../../../utils/formatCoverage";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import type { SowStatus } from "../../../../types";

export type SowPeriodTone =
  | "muted"
  | "neutral"
  | "success"
  | "brand"
  | "danger";

export interface FormattedSowPeriod {
  /** `15 Apr 2026 → 30 Sep 2026`. */
  range: string;
  /** Proximity meta-line; tone-aware. */
  meta: string;
  tone: SowPeriodTone;
}

function fmtDay(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function isoDayUTC(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((isoDayUTC(toIso) - isoDayUTC(fromIso)) / 86_400_000);
}

export interface FormatSowPeriodInput {
  startDate: string;
  endDate: string;
  status: SowStatus;
  /** Brisbane-today override for tests. Defaults to the tracking-system
   *  Brisbane today helper so tests can pin the date deterministically. */
  today?: string;
}

export function formatSowPeriod({
  startDate,
  endDate,
  status,
  today = getBrisbaneToday(),
}: FormatSowPeriodInput): FormattedSowPeriod {
  const range = `${fmtDay(startDate)} → ${fmtDay(endDate)}`;

  if (status === "cancelled") {
    return { range, meta: "cancelled", tone: "muted" };
  }

  if (status === "completed") {
    const daysSinceEnd = daysBetween(endDate, today);
    if (daysSinceEnd >= 0) {
      return {
        range,
        meta: `completed ${humaniseDays(daysSinceEnd)} ago`,
        tone: "success",
      };
    }
    return { range, meta: "completed", tone: "success" };
  }

  // draft + active branch — date-driven proximity cue.
  const daysToStart = daysBetween(today, startDate);
  const daysToEnd = daysBetween(today, endDate);

  if (daysToStart > 0) {
    return {
      range,
      meta: `starts in ${humaniseDays(daysToStart)}`,
      tone: status === "draft" ? "neutral" : "brand",
    };
  }

  if (daysToEnd < 0) {
    // Past end date but stored status is still draft / active. Brief
    // §6 calls for `overdue · Nd past end date` here.
    return {
      range,
      meta: `overdue · ${humaniseDays(-daysToEnd)} past end date`,
      tone: "danger",
    };
  }

  // In-progress (today is between start and end inclusive).
  if (status === "active") {
    return {
      range,
      meta: `in progress · ${humaniseDays(daysToEnd)} remaining`,
      tone: "success",
    };
  }

  // draft and within the start/end window — unusual but possible (created
  // late). Show the time-remaining cue without the in-progress label.
  return {
    range,
    meta: `${humaniseDays(daysToEnd)} remaining`,
    tone: "neutral",
  };
}
